/**
 * Weak-mode detection.
 *
 * CR-01 §8 supersedes Section 06's thresholds with a deterministic heuristic:
 * enter on two probe soft deadlines within 60 s, three consecutive round trips
 * above 1,500 ms, or two failed connection rounds within 60 s; exit only after
 * 60 continuous seconds without those failures and five consecutive round trips
 * below 800 ms. An explicit low-data selection still overrides recovery.
 *
 * This is messaging transfer policy and nothing more. It decides how many
 * upload parts run at once and how long a request may take; it is not a
 * congestion controller and it never touches the WebSocket.
 *
 * The module works in both a window and a worker, because the transfer manager
 * runs in the worker and the download confirmation runs on the page, and both
 * have to agree about what "metered" means.
 */

/**
 * The Network Information API.
 *
 * It is not in the DOM lib types, and it is absent in Firefox and Safari
 * entirely. Declaring the shape here rather than reaching for `any` keeps the
 * absence a checked `undefined` instead of an unchecked property access on a
 * value the compiler has stopped reasoning about.
 */
interface NetworkInformationLike {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
  readonly type?: string;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

function connectionInfo(): NetworkInformationLike | undefined {
  const scope = globalThis as { navigator?: { connection?: NetworkInformationLike } };
  return scope.navigator?.connection;
}

/**
 * Whether the operating system reports a metered or low-data connection.
 *
 * `saveData` is the user asking for less data and is treated as authoritative.
 * `effectiveType` of 2g or slow-2g is the browser's own estimate of a link too
 * slow for full media, which is the same decision Section 06 is making.
 */
export function isMeteredOrLowData(): boolean {
  const info = connectionInfo();
  if (!info) {
    return false;
  }
  if (info.saveData === true) {
    return true;
  }
  const effective = info.effectiveType ?? '';
  if (effective === '2g' || effective === 'slow-2g') {
    return true;
  }
  return info.type === 'cellular';
}

export type NetworkMode = 'normal' | 'weak';

/*
 * CR-01 §8 fixes the weak-mode heuristic, superseding the earlier Section 06
 * thresholds. It is deliberately not bandwidth estimation: the objective is to
 * remove application-induced contention and recover from real loss, not to
 * guarantee a quality of service on somebody else's access network.
 *
 * Enter on any of: two probe soft-deadline events within 60 s, three consecutive
 * matched round trips above 1,500 ms, or two failed connection rounds within
 * 60 s. Exit only after 60 continuous seconds without those failures AND five
 * consecutive matched round trips below 800 ms -- both, because a single fast
 * sample on a bad link proves nothing.
 */
const RTT_WEAK_MS = 1_500;
const RTT_RECOVERY_MS = 800;
const RTT_SAMPLES_TO_ENTER = 3;
const RTT_SAMPLES_TO_EXIT = 5;
const RECOVERY_WINDOW_MS = 60_000;
const FAILURE_WINDOW_MS = 60_000;
const FAILURES_TO_ENTER = 2;

export class NetworkMonitor {
  private mode: NetworkMode = 'normal';
  private consecutiveSlowSamples = 0;
  /** §8: five consecutive fast samples are required to leave weak mode. */
  private consecutiveFastSamples = 0;
  /** When the current run of fast samples with no failures began. */
  private healthySince: number | null = null;
  private readonly recentFailures: number[] = [];
  private readonly listeners = new Set<(mode: NetworkMode) => void>();

  constructor() {
    // A saveData toggle or a radio change should take effect immediately
    // rather than at the next heartbeat.
    connectionInfo()?.addEventListener?.('change', () => {
      this.reevaluate();
    });
    this.reevaluate();
  }

  get current(): NetworkMode {
    return this.mode;
  }

  onChange(listener: (mode: NetworkMode) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** One application round-trip measurement, from the transport heartbeat. */
  recordRoundTrip(rttMs: number): void {
    if (rttMs > RTT_WEAK_MS) {
      this.consecutiveSlowSamples += 1;
      this.consecutiveFastSamples = 0;
      this.healthySince = null;
    } else {
      this.consecutiveSlowSamples = 0;
      if (rttMs < RTT_RECOVERY_MS) {
        this.consecutiveFastSamples += 1;
        if (this.healthySince === null) {
          this.healthySince = Date.now();
        }
      } else {
        // Between the two thresholds: not slow enough to enter, not fast enough
        // to count towards leaving. §8 requires five consecutive samples below
        // 800 ms, so a middling one resets that run without starting a new
        // failure.
        this.consecutiveFastSamples = 0;
        this.healthySince = null;
      }
    }
    this.reevaluate();
  }

  /**
   * §8: a probe soft deadline expired. Two within 60 seconds enter weak mode.
   *
   * This is a connection-level signal rather than a transfer failure, and it is
   * counted in the same window because it means the same thing: the link is not
   * carrying our traffic reliably.
   */
  recordProbeSoftDeadline(): void {
    this.recordFailureAt(Date.now());
  }

  /** §8: a connection round failed. Two within 60 seconds enter weak mode. */
  recordConnectionRoundFailure(): void {
    this.recordFailureAt(Date.now());
  }

  private recordFailureAt(now: number): void {
    this.recentFailures.push(now);
    this.healthySince = null;
    this.consecutiveFastSamples = 0;
    this.pruneFailures(now);
    this.reevaluate();
  }

  /** A media transfer failed. Two inside the failure window enter weak mode. */
  recordTransferFailure(): void {
    this.recordFailureAt(Date.now());
  }

  private pruneFailures(now: number): void {
    while (this.recentFailures.length > 0 && now - (this.recentFailures[0] ?? 0) > FAILURE_WINDOW_MS) {
      this.recentFailures.shift();
    }
  }

  private reevaluate(): void {
    const now = Date.now();
    this.pruneFailures(now);
    const lowData = isMeteredOrLowData();

    let next: NetworkMode;
    if (lowData) {
      // The explicit low-data selection overrides recovery entirely: Section 06
      // exits weak mode "unless low-data remains selected".
      next = 'weak';
    } else if (
      this.consecutiveSlowSamples >= RTT_SAMPLES_TO_ENTER ||
      this.recentFailures.length >= FAILURES_TO_ENTER
    ) {
      next = 'weak';
    } else if (this.mode === 'weak') {
      // §8: both conditions, not either. Sixty quiet seconds with a slow link
      // is still a slow link, and five fast samples in the middle of a flapping
      // network is noise.
      const recovered =
        this.healthySince !== null &&
        now - this.healthySince >= RECOVERY_WINDOW_MS &&
        this.consecutiveFastSamples >= RTT_SAMPLES_TO_EXIT &&
        this.recentFailures.length === 0;
      next = recovered ? 'normal' : 'weak';
    } else {
      next = 'normal';
    }

    if (next !== this.mode) {
      this.mode = next;
      for (const listener of this.listeners) {
        listener(next);
      }
    }
  }
}

/**
 * A byte-rate limiter for background transfers.
 *
 * Section 06: "Background upload requests use a client token bucket: 256 KiB/s
 * normal mode, 64 KiB/s weak mode; an explicitly foregrounded file transfer may
 * use the measured available bandwidth while retaining text priority."
 *
 * The bucket paces *whole parts*: a part is admitted once the bucket holds
 * enough tokens for it. Shaping the body byte by byte would need a streaming
 * request, which is not available for a `fetch` upload in every browser this
 * client supports, and pacing at part granularity produces the same average
 * rate over any window longer than one part.
 */
export class TokenBucket {
  private tokens: number;
  private lastRefill = Date.now();
  private ratePerSecond: number;

  constructor(ratePerSecond: number) {
    this.ratePerSecond = ratePerSecond;
    // Starts full so the first part of a fresh transfer is not delayed by a
    // budget the client has not had time to earn.
    this.tokens = ratePerSecond;
  }

  setRate(ratePerSecond: number): void {
    this.refill();
    this.ratePerSecond = ratePerSecond;
    this.tokens = Math.min(this.tokens, ratePerSecond);
  }

  private refill(): void {
    const now = Date.now();
    const elapsed = (now - this.lastRefill) / 1000;
    this.lastRefill = now;
    // One second of burst, so a paused transfer does not accumulate an
    // unbounded allowance and then flood the link when it resumes.
    this.tokens = Math.min(this.ratePerSecond, this.tokens + elapsed * this.ratePerSecond);
  }

  /** Milliseconds to wait before `bytes` may be sent. Zero when it may go now. */
  delayFor(bytes: number): number {
    this.refill();
    if (this.tokens >= bytes) {
      this.tokens -= bytes;
      return 0;
    }
    const deficit = bytes - this.tokens;
    this.tokens = 0;
    return Math.ceil((deficit / this.ratePerSecond) * 1000);
  }
}

/** Section 06 background rates. */
export const BACKGROUND_RATE_NORMAL = 256 * 1024;
export const BACKGROUND_RATE_WEAK = 64 * 1024;
